/** Client-only job create draft snapshot (per hiring role id) — until API-backed. */

const STORAGE_PREFIX = "jobuwin-hiring-job-draft-";

export function hiringJobDraftStorageKey(roleId) {
  return `${STORAGE_PREFIX}${roleId}`;
}

const FORM_KEYS = [
  "title",
  "functionalArea",
  "country",
  "city",
  "seniorityLevel",
  "workMode",
  "employmentType",
  "salaryCurrency",
  "salaryMin",
  "salaryMax",
  "summary",
  "responsibilities",
  "requirements",
  "defaultApplyAudience",
  "defaultApplyCountryCodes",
];

function pickFormFields(formData) {
  const out = {};
  FORM_KEYS.forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(formData, k)) {
      out[k] = formData[k];
    }
  });
  return out;
}

const EMPTY_QUILL_SNAPSHOT = "<p><br></p>";

function snapshotLooksNonEmpty(fields) {
  return FORM_KEYS.some((k) => {
    const v = fields[k];
    if (v == null) return false;
    const s = String(v).trim();
    if (!s) return false;
    if ((k === "requirements" || k === "responsibilities") && s === EMPTY_QUILL_SNAPSHOT) return false;
    return true;
  });
}

/** Shown when there is no localStorage snapshot yet (first open of a demo draft). */
export const HIRING_JOB_DRAFT_SEEDS = {
  "r-4": {
    title: "Engineering Manager",
    functionalArea: "Technology",
    country: "GB",
    city: "Edinburgh",
    seniorityLevel: "manager",
    workMode: "hybrid",
    employmentType: "full-time",
    salaryCurrency: "GBP",
    salaryMin: "95000",
    salaryMax: "115000",
    summary:
      "Lead cross-functional engineering teams, own delivery and quality for our product platform, and partner with Product to grow the roadmap.",
    responsibilities:
      "<p>Scale the engineering org, set clear technical direction, and foster a culture of ownership and shipping. You will work with stakeholders across the business to prioritise outcomes and manage risk.</p>",
    requirements:
      "<ul><li>Experience leading software teams in fast-paced product companies.</li><li>Strong communication and stakeholder management.</li><li>Comfort with hybrid work in the UK.</li></ul>",
  },
};

export function loadHiringJobDraft(roleId, initialForm) {
  if (!roleId) return { ...initialForm };
  const base = { ...initialForm };
  try {
    const raw = window.localStorage.getItem(hiringJobDraftStorageKey(roleId));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        const merged = { ...base, ...pickFormFields(parsed) };
        if (!String(merged.functionalArea || "").trim() && parsed.department != null) {
          merged.functionalArea = String(parsed.department);
        }
        return merged;
      }
    }
  } catch {
    /* ignore */
  }
  const seed = HIRING_JOB_DRAFT_SEEDS[roleId];
  if (seed) {
    return { ...base, ...pickFormFields(seed) };
  }
  return base;
}

export function saveHiringJobDraft(roleId, formData) {
  if (!roleId) return;
  try {
    window.localStorage.setItem(hiringJobDraftStorageKey(roleId), JSON.stringify(pickFormFields(formData)));
  } catch {
    /* ignore */
  }
}

const JOB_CREATE_AUTH_RECOVERY_KEY = "jobuwin-hiring-job-create-auth-recovery";

/**
 * Persist job-create form when the user hits an auth error so it can be restored after signing in again.
 * Cleared after a successful draft save, publish, or server draft load.
 */
export function saveHiringJobCreateAuthRecovery(formData, savedPitchUrl) {
  try {
    const snapshot = {
      ...pickFormFields(formData),
      savedPitchUrl: String(savedPitchUrl || "").trim(),
      savedAt: Date.now(),
    };
    window.localStorage.setItem(JOB_CREATE_AUTH_RECOVERY_KEY, JSON.stringify(snapshot));
  } catch {
    /* ignore */
  }
}

/** @returns {{ fields: Record<string, unknown>, savedPitchUrl: string } | null} */
export function loadHiringJobCreateAuthRecovery() {
  try {
    const raw = window.localStorage.getItem(JOB_CREATE_AUTH_RECOVERY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const fields = pickFormFields(parsed);
    if (!snapshotLooksNonEmpty(fields)) return null;
    const savedPitchUrl = typeof parsed.savedPitchUrl === "string" ? parsed.savedPitchUrl : "";
    return { fields, savedPitchUrl };
  } catch {
    return null;
  }
}

const JOB_CREATE_PREVIEW_RESUME_KEY = "jobuwin-hiring-job-create-preview-resume-v1";

const PREVIEW_RESUME_MAX_AGE_MS = 60 * 60 * 1000;

const IDB_NAME = "jobuwin-hiring-job-create";
const IDB_VERSION = 1;
const IDB_STORE = "previewPitch";
const IDB_PITCH_RECORD_KEY = "jobCreatePitchV1";

function idbSupported() {
  return typeof indexedDB !== "undefined";
}

function openJobCreatePreviewPitchDb() {
  if (!idbSupported()) return Promise.reject(new Error("IndexedDB unavailable"));
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, IDB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE);
    };
  });
}

async function idbPutJobCreatePreviewPitch(blob) {
  if (!(blob instanceof Blob) || blob.size === 0) return;
  const db = await openJobCreatePreviewPitchDb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(IDB_STORE).put(blob, IDB_PITCH_RECORD_KEY);
    });
  } finally {
    db.close();
  }
}

async function idbTakeJobCreatePreviewPitch() {
  const db = await openJobCreatePreviewPitchDb();
  try {
    return await new Promise((resolve, reject) => {
      let taken = null;
      const tx = db.transaction(IDB_STORE, "readwrite");
      const store = tx.objectStore(IDB_STORE);
      const g = store.get(IDB_PITCH_RECORD_KEY);
      g.onerror = () => reject(g.error);
      g.onsuccess = () => {
        taken = g.result ?? null;
        store.delete(IDB_PITCH_RECORD_KEY);
      };
      tx.oncomplete = () => resolve(taken instanceof Blob && taken.size > 0 ? taken : null);
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

async function idbClearJobCreatePreviewPitch() {
  if (!idbSupported()) return;
  try {
    const db = await openJobCreatePreviewPitchDb();
    try {
      await new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, "readwrite");
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.objectStore(IDB_STORE).delete(IDB_PITCH_RECORD_KEY);
      });
    } finally {
      db.close();
    }
  } catch {
    /* ignore */
  }
}

export function hasPendingHiringJobCreatePreviewResume() {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(sessionStorage.getItem(JOB_CREATE_PREVIEW_RESUME_KEY));
  } catch {
    return false;
  }
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const data = String(r.result || "");
      const i = data.indexOf(",");
      resolve(i >= 0 ? data.slice(i + 1) : data);
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

async function getRecordedPitchBlobForPreviewResume(savedPitchUrl, pitchPendingBlob, pitchPendingMime) {
  const url = String(savedPitchUrl || "").trim();
  if (url.startsWith("blob:")) {
    try {
      const blob = await fetch(url).then((res) => res.blob());
      return {
        blob: blob instanceof Blob && blob.size > 0 ? blob : null,
        mimeHint: blob?.type || pitchPendingMime || "",
      };
    } catch {
      return { blob: null, mimeHint: "" };
    }
  }
  if (pitchPendingBlob instanceof Blob && pitchPendingBlob.size > 0) {
    return {
      blob: pitchPendingBlob,
      mimeHint: String(pitchPendingMime || pitchPendingBlob.type || ""),
    };
  }
  return { blob: null, mimeHint: "" };
}

function decodeEmbeddedBlobPitch(encoded) {
  if (!encoded || typeof encoded !== "object") return "";
  if (encoded.kind === "blob" && typeof encoded.base64 === "string" && encoded.base64) {
    try {
      const bin = atob(encoded.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
      const mime = String(encoded.mime || "video/webm").trim() || "video/webm";
      return URL.createObjectURL(new Blob([bytes], { type: mime }));
    } catch {
      return "";
    }
  }
  return "";
}

async function decodePitchEncodedForPreviewResumeAsync(encoded) {
  if (!encoded || typeof encoded !== "object") return "";
  if (encoded.kind === "url" && typeof encoded.url === "string" && encoded.url.trim()) {
    return encoded.url.trim();
  }
  if (encoded.kind === "idb") {
    const blob = await idbTakeJobCreatePreviewPitch();
    if (!(blob instanceof Blob) || blob.size === 0) return "";
    return URL.createObjectURL(blob);
  }
  return decodeEmbeddedBlobPitch(encoded);
}

/** Blob pitches persist in IndexedDB; remote URLs stay in session JSON only. Falls back to small base64 in session when IDB fails. */
export async function saveHiringJobCreatePreviewResume(
  navSearch,
  formData,
  baselineFingerprint,
  savedPitchUrl,
  pitchPendingBlobRef,
  pitchPendingMimeRef
) {
  await idbClearJobCreatePreviewPitch();
  const pendingBlob =
    pitchPendingBlobRef && pitchPendingBlobRef.current instanceof Blob ? pitchPendingBlobRef.current : null;
  const pendingMime = pitchPendingMimeRef && pitchPendingMimeRef.current ? pitchPendingMimeRef.current : "";
  const url = String(savedPitchUrl || "").trim();

  /** @type {{ kind: string, url?: string, mime?: string, base64?: string }} */
  let pitchEncoded = { kind: "none" };

  if (url && !url.startsWith("blob:")) {
    pitchEncoded = { kind: "url", url };
  } else {
    try {
      const { blob, mimeHint } = await getRecordedPitchBlobForPreviewResume(url, pendingBlob, pendingMime);
      if (blob && blob.size > 0 && idbSupported()) {
        try {
          await idbPutJobCreatePreviewPitch(blob);
          pitchEncoded = { kind: "idb" };
        } catch {
          pitchEncoded = { kind: "none" };
        }
      }
      if (pitchEncoded.kind === "none" && blob && blob.size > 0) {
        try {
          const base64 = await blobToBase64(blob);
          const mime = String(mimeHint || blob.type || "video/webm").trim() || "video/webm";
          if (base64.length < 2_000_000) {
            pitchEncoded = { kind: "blob", mime, base64 };
          }
        } catch {
          pitchEncoded = { kind: "none" };
        }
      }
    } catch {
      pitchEncoded = { kind: "none" };
    }
  }

  const snapshot = {
    v: 1,
    savedAt: Date.now(),
    navSearch: navSearch == null ? "" : String(navSearch),
    baselineFingerprint: String(baselineFingerprint ?? ""),
    fields: pickFormFields(formData),
    pitchEncoded,
  };
  let toStore = snapshot;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const json = JSON.stringify(toStore);
      if (json.length > 4_500_000) throw new Error("oversize");
      sessionStorage.setItem(JOB_CREATE_PREVIEW_RESUME_KEY, json);
      return;
    } catch {
      await idbClearJobCreatePreviewPitch();
      toStore = { ...snapshot, pitchEncoded: { kind: "none" } };
    }
  }
}

export function clearHiringJobCreatePreviewResume() {
  try {
    sessionStorage.removeItem(JOB_CREATE_PREVIEW_RESUME_KEY);
  } catch {
    /* ignore */
  }
  void idbClearJobCreatePreviewPitch();
}

/**
 * Restore once for matching `navSearch` (typically `window.location.search` on `/hiring/jobs/new`).
 * @returns {Promise<{ fields: Record<string, unknown>, baselineFingerprint: string, savedPitchUrl: string } | null>}
 */
export async function consumeHiringJobCreatePreviewResumeAsync(navSearchNow) {
  try {
    const raw = sessionStorage.getItem(JOB_CREATE_PREVIEW_RESUME_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || parsed.v !== 1) {
      sessionStorage.removeItem(JOB_CREATE_PREVIEW_RESUME_KEY);
      await idbClearJobCreatePreviewPitch();
      return null;
    }
    if (!parsed.savedAt || Date.now() - parsed.savedAt > PREVIEW_RESUME_MAX_AGE_MS) {
      sessionStorage.removeItem(JOB_CREATE_PREVIEW_RESUME_KEY);
      await idbClearJobCreatePreviewPitch();
      return null;
    }
    const want = navSearchNow == null ? "" : String(navSearchNow);
    const got = parsed.navSearch == null ? "" : String(parsed.navSearch);
    if (want !== got) {
      sessionStorage.removeItem(JOB_CREATE_PREVIEW_RESUME_KEY);
      await idbClearJobCreatePreviewPitch();
      return null;
    }
    let savedPitchUrl = "";
    try {
      savedPitchUrl = await decodePitchEncodedForPreviewResumeAsync(parsed.pitchEncoded);
    } catch {
      savedPitchUrl = "";
    }
    sessionStorage.removeItem(JOB_CREATE_PREVIEW_RESUME_KEY);
    const fieldObj = parsed.fields && typeof parsed.fields === "object" ? parsed.fields : {};
    return {
      fields: fieldObj,
      baselineFingerprint: String(parsed.baselineFingerprint || ""),
      savedPitchUrl,
    };
  } catch {
    try {
      sessionStorage.removeItem(JOB_CREATE_PREVIEW_RESUME_KEY);
    } catch {
      /* ignore */
    }
    await idbClearJobCreatePreviewPitch();
    return null;
  }
}

export function clearHiringJobCreateAuthRecovery() {
  try {
    window.localStorage.removeItem(JOB_CREATE_AUTH_RECOVERY_KEY);
  } catch {
    /* ignore */
  }
  clearHiringJobCreatePreviewResume();
}

/** True when `?draft=` is an API job post id (UUID), not a hiring-role draft id. */
export function isJobPostDraftQueryId(value) {
  const s = String(value || "").trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

/**
 * Hiring-role draft id from `?draft=<roleId>` or `state.draftRoleId`.
 * Excludes API job post UUIDs (`?draft=<jobPostId>`) used on the create page after saving a server draft.
 */
export function resolveHiringDraftRoleId(location) {
  if (!location) return null;
  try {
    const params = new URLSearchParams(location.search || "");
    const q = params.get("draft");
    if (q && !isJobPostDraftQueryId(q)) return q;
  } catch {
    /* ignore */
  }
  const s = location.state;
  if (s && typeof s === "object" && s.draftRoleId != null && s.draftRoleId !== "") {
    return String(s.draftRoleId);
  }
  return null;
}
